import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { fetchMyClient } from '@/features/clients/api';
import { ALL_CLIENT_ROLES } from '@/types/auth';

/**
 * Route guard for /portal/* pages.
 *
 * Redirects ONBOARDING clients to /portal/overview so they complete workspace
 * activation (API key generation) before accessing any other portal page.
 *
 * - Only runs for CLIENT roles; non-client users pass through unchanged.
 * - Uses the cached ['me', 'myClient'] query — no extra network round-trips
 *   once ClientOverviewPage or any earlier component has fetched it.
 * - Returns null while loading (instant on cache hit; brief flash on first load).
 */
export function ClientPortalGuard({ children }: { children: ReactNode }) {
  const { hasAnyRole } = useAuth();
  const location = useLocation();
  const isClientUser = hasAnyRole(ALL_CLIENT_ROLES);

  const clientQuery = useQuery({
    queryKey: ['me', 'myClient'],
    queryFn: fetchMyClient,
    staleTime: 30_000,
    enabled: isClientUser,
  });

  // Non-client users (admins navigating a shared AppLayout path) pass through.
  if (!isClientUser) return <>{children}</>;

  // While status is loading, render nothing (data is usually cached → instant).
  if (clientQuery.isPending) return null;

  // ONBOARDING clients must complete activation first.
  if (
    clientQuery.data?.clientStatus === 'ONBOARDING' &&
    location.pathname !== '/portal/overview'
  ) {
    return <Navigate to="/portal/overview" replace />;
  }

  return <>{children}</>;
}
